import React from 'react';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import { TripRequest } from '@marageti/z4-sdk/lib/travel/TripRequest';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel/TripMatch';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { Review } from '@marageti/z4-sdk/lib/travel';
import TravelHistoryModalView from './travel-history-modal-view';
import { TravelHistoryModalContainerProps } from './types';

export const useTravelHistoryTripRequests = (travelerId: string) => {
  const apiClient = useApiClient();
  const { data: tripRequests, isLoading: isTripRequestsLoading } = useFetch<SearchResult<TripRequest>>(apiClient.tripRequestsClient.postTripRequestSearches, [{
    filterBy: [{ field: 'travelers.id', value: [travelerId] }],
    sortBy: [{ field: 'createdAt', order: 'desc' }],
    pagination: { limit: 10000, page: 1 },
  }], !!travelerId);

  return { tripRequests, isTripRequestsLoading, totalHits: tripRequests?.totalHits || 0 };
};

export const useTravelHistorySoldTrips = (travelerId: string) => {
  const apiClient = useApiClient();
  const { data: soldTrips, isLoading: isSoldTripsLoading } = useFetch<SearchResult<TripMatch>>(apiClient.tripMatchesClient.postTripMatchSearches, [{
    filterBy: [{ field: 'travelers.id', value: [travelerId] }, { field: 'status', value: [TripMatchStatus.MatchSold] }],
    sortBy: [{ field: 'departureDate', order: 'desc' }],
    pagination: { limit: 10000, page: 1 },
  }], !!travelerId);

  return { soldTrips, isSoldTripsLoading, totalHits: soldTrips?.totalHits || 0 };
};

export const useTravelHistoryReviews = (travelerId: string) => {
  const apiClient = useApiClient();
  const { data: reviews, isLoading: isReviewsLoading } = useFetchImmutable<SearchResult<Review>>(apiClient.reviewsClient.postReviewsSearch, [{
    filterBy: [{ field: 'travelerId', value: [travelerId] }],
    pagination: { limit: 10000, page: 1 },
  }], !!travelerId);

  return { reviews, isReviewsLoading, totalHits: reviews?.totalHits || 0 };
};

const TravelHistoryModalContainer = ({
  open,
  traveler,
  selectedTab,
  onTabChange,
  onClose,
}: TravelHistoryModalContainerProps) => {
  const apiClient = useApiClient();
  const { tripRequests, isTripRequestsLoading } = useTravelHistoryTripRequests(traveler.id);
  const { soldTrips, isSoldTripsLoading } = useTravelHistorySoldTrips(traveler.id);
  const { reviews, isReviewsLoading } = useTravelHistoryReviews(traveler.id);

  const {
    data: tripMatches,
    isLoading: isTripMatchesLoading,
  } = useFetch<SearchResult<TripMatch>>(apiClient.tripMatchesClient.postTripMatchSearches, [{
    filterBy: [{ field: 'travelers.id', value: [traveler.id] }],
    sortBy: [{ field: 'departureDate', order: 'desc' }],
    pagination: { limit: 10000, page: 1 },
  }], true);

  return (
    <TravelHistoryModalView
      open={open}
      selectedTab={selectedTab}
      handleTabChange={onTabChange}
      handleClose={onClose}
      travelerId={traveler.id}
      tripRequests={tripRequests?.hits}
      tripMatches={tripMatches?.hits}
      soldTrips={soldTrips?.hits}
      reviews={reviews?.hits}
      isLoading={isTripRequestsLoading || isTripMatchesLoading || isSoldTripsLoading || isReviewsLoading}
    />
  );
};

export default TravelHistoryModalContainer;
